import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { categories, videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [sortBy, setSortBy] = useState<'rating' | 'latest' | 'popular'>('rating');

  const filteredVideos = useMemo(() => {
    let result = videos.filter((v) => v.categorySlug === currentCategory.slug);
    if (sortBy === 'rating') result = [...result].sort((a, b) => b.rating - a.rating);
    if (sortBy === 'latest') result = [...result].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    if (sortBy === 'popular') result = [...result].sort((a, b) => parseFloat(b.views.replace('亿', '0000')) - parseFloat(a.views.replace('亿', '0000')));
    return result;
  }, [currentCategory.slug, sortBy]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 棒棒TV`,
    description: currentCategory.description,
    url: `https://www.bangbangtv.com/category/${currentCategory.slug}`,
    hasPart: filteredVideos.map((v) => ({
      '@type': 'CreativeWork',
      name: v.title,
      url: `https://www.bangbangtv.com/article/${v.id}`,
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${currentCategory.name}`}
        description={`棒棒TV${currentCategory.name}频道 - ${currentCategory.description}。免费高清视频流畅播放，在线不缓冲秒播，即点即看零等待。`}
        keywords={`${currentCategory.name},在线不缓冲,秒播,棒棒TV,免费高清视频,流畅播放`}
        canonicalUrl={`https://www.bangbangtv.com/category/${currentCategory.slug}`}
      />
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
                <div className="flex bg-background-100 rounded-lg p-1">
                  {[
                    { key: 'rating' as const, label: '评分最高' },
                    { key: 'latest' as const, label: '最新上线' },
                    { key: 'popular' as const, label: '最受欢迎' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md cursor-pointer transition-colors whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-background-50 text-primary-500 shadow-sm'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                    cat.slug === currentCategory.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5 pb-12">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}